import React, { useState, useRef } from 'react';
import { X, Download, FileText, Check, Loader2, BarChart3, Clock, MessageSquare, TrendingUp } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

const ExportPDFModal = ({ 
  isOpen, 
  onClose, 
  talkTimeData, 
  keywordData, 
  outcomeData, 
  trendsData, 
  days 
}) => {
  const [selectedSections, setSelectedSections] = useState({
    overview: true,
    talkTime: true,
    keywords: true,
    outcomes: true,
    dailyTrends: true
  });
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeTables, setIncludeTables] = useState(true);
  const [generating, setGenerating] = useState(false);
  const chartRefs = useRef({});

  const sections = [
    { id: 'overview', label: 'Overview & Key Metrics', icon: BarChart3 },
    { id: 'talkTime', label: 'Talk Time Analysis', icon: Clock },
    { id: 'keywords', label: 'Keyword Detection', icon: MessageSquare },
    { id: 'outcomes', label: 'Call Outcomes & Funnel', icon: TrendingUp },
    { id: 'dailyTrends', label: 'Daily Trends', icon: BarChart3 }
  ];

  const toggleSection = (id) => {
    setSelectedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const generatePDF = async () => {
    setGenerating(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // ==================== HEADER WITH BRANDING ====================
      // Purple header bar
      pdf.setFillColor(139, 92, 246); // Purple-500
      pdf.rect(0, 0, pageWidth, 35, 'F');

      // AIConneqtors Logo (text-based)
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AIConneqtors', margin, 18);

      // Subtitle
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Voice Analytics Report', margin, 26);

      // Date range on right
      const dateRange = `Last ${days} Days`;
      const reportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      pdf.setFontSize(9);
      pdf.text(dateRange, pageWidth - margin, 18, { align: 'right' });
      pdf.text(`Generated: ${reportDate}`, pageWidth - margin, 26, { align: 'right' });

      yPos = 45;

      // ==================== OVERVIEW SECTION ====================
      if (selectedSections.overview && talkTimeData) {
        pdf.setTextColor(30, 30, 30);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Overview', margin, yPos);
        yPos += 10;

        if (includeTables) {
          // Key Metrics Table
          const metricsData = [
            ['Total Calls', String(talkTimeData.total_calls || 0)],
            ['Total Talk Time', `${talkTimeData.metrics?.total_talk_time_hours || 0} hours`],
            ['Average Duration', `${talkTimeData.metrics?.avg_duration_seconds || 0} seconds`],
            ['Answer Rate', `${outcomeData?.conversion_funnel?.answered_rate || 0}%`],
            ['Transfer Rate', `${outcomeData?.transfer_analysis?.transfer_rate_percent || 0}%`]
          ];

          pdf.autoTable({
            startY: yPos,
            head: [['Metric', 'Value']],
            body: metricsData,
            theme: 'striped',
            headStyles: { fillColor: [139, 92, 246], textColor: 255 },
            styles: { fontSize: 10, cellPadding: 4 },
            margin: { left: margin, right: margin }
          });
          yPos = pdf.lastAutoTable.finalY + 15;
        }
      }

      // ==================== TALK TIME SECTION ====================
      if (selectedSections.talkTime && talkTimeData) {
        // Check if we need a new page
        if (yPos > pageHeight - 80) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setTextColor(30, 30, 30);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Talk Time Analysis', margin, yPos);
        yPos += 10;

        if (includeTables) {
          // Duration Distribution Table
          const durationData = talkTimeData.duration_distribution 
            ? Object.entries(talkTimeData.duration_distribution).map(([bucket, count]) => [bucket, String(count)])
            : [];

          if (durationData.length > 0) {
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');
            pdf.text('Call Duration Distribution', margin, yPos);
            yPos += 5;

            pdf.autoTable({
              startY: yPos,
              head: [['Duration Range', 'Number of Calls']],
              body: durationData,
              theme: 'striped',
              headStyles: { fillColor: [59, 130, 246], textColor: 255 },
              styles: { fontSize: 10, cellPadding: 4 },
              margin: { left: margin, right: margin }
            });
            yPos = pdf.lastAutoTable.finalY + 10;
          }

          // Hourly Distribution Table
          const hourlyData = talkTimeData.hourly_distribution
            ? Object.entries(talkTimeData.hourly_distribution)
                .filter(([_, count]) => count > 0)
                .map(([hour, count]) => [hour, String(count)])
            : [];

          if (hourlyData.length > 0) {
            if (yPos > pageHeight - 60) {
              pdf.addPage();
              yPos = margin;
            }

            pdf.setFontSize(12);
            pdf.text('Hourly Call Distribution', margin, yPos);
            yPos += 5;

            pdf.autoTable({
              startY: yPos,
              head: [['Hour', 'Number of Calls']],
              body: hourlyData,
              theme: 'striped',
              headStyles: { fillColor: [139, 92, 246], textColor: 255 },
              styles: { fontSize: 10, cellPadding: 3 },
              margin: { left: margin, right: margin }
            });
            yPos = pdf.lastAutoTable.finalY + 15;
          }
        }
      }

      // ==================== KEYWORDS SECTION ====================
      if (selectedSections.keywords && keywordData) {
        if (yPos > pageHeight - 80) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setTextColor(30, 30, 30);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Keyword Detection', margin, yPos);
        yPos += 10;

        if (includeTables) {
          // Top Keywords Table
          const keywordsTableData = keywordData.top_keywords
            ? keywordData.top_keywords.slice(0, 15).map(kw => [kw.keyword, String(kw.count)])
            : [];

          if (keywordsTableData.length > 0) {
            pdf.autoTable({
              startY: yPos,
              head: [['Keyword', 'Occurrences']],
              body: keywordsTableData,
              theme: 'striped',
              headStyles: { fillColor: [16, 185, 129], textColor: 255 },
              styles: { fontSize: 10, cellPadding: 4 },
              margin: { left: margin, right: margin }
            });
            yPos = pdf.lastAutoTable.finalY + 10;
          }

          // Sentiment Distribution
          if (keywordData.sentiment_distribution) {
            if (yPos > pageHeight - 50) {
              pdf.addPage();
              yPos = margin;
            }

            pdf.setFontSize(12);
            pdf.text('Sentiment Distribution', margin, yPos);
            yPos += 5;

            const sentimentData = Object.entries(keywordData.sentiment_distribution)
              .map(([sentiment, count]) => [
                sentiment.charAt(0).toUpperCase() + sentiment.slice(1),
                String(count)
              ]);

            pdf.autoTable({
              startY: yPos,
              head: [['Sentiment', 'Count']],
              body: sentimentData,
              theme: 'striped',
              headStyles: { fillColor: [245, 158, 11], textColor: 255 },
              styles: { fontSize: 10, cellPadding: 4 },
              margin: { left: margin, right: margin }
            });
            yPos = pdf.lastAutoTable.finalY + 15;
          }
        }
      }

      // ==================== OUTCOMES SECTION ====================
      if (selectedSections.outcomes && outcomeData) {
        if (yPos > pageHeight - 80) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setTextColor(30, 30, 30);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Call Outcomes', margin, yPos);
        yPos += 10;

        if (includeTables) {
          // Conversion Funnel
          if (outcomeData.conversion_funnel) {
            const funnelData = [
              ['Initiated', String(outcomeData.conversion_funnel.initiated || 0)],
              ['Answered', String(outcomeData.conversion_funnel.answered || 0)],
              ['Completed', String(outcomeData.conversion_funnel.completed || 0)],
              ['Answer Rate', `${outcomeData.conversion_funnel.answered_rate || 0}%`],
              ['Completion Rate', `${outcomeData.conversion_funnel.completion_rate || 0}%`]
            ];

            pdf.autoTable({
              startY: yPos,
              head: [['Stage', 'Value']],
              body: funnelData,
              theme: 'striped',
              headStyles: { fillColor: [139, 92, 246], textColor: 255 },
              styles: { fontSize: 10, cellPadding: 4 },
              margin: { left: margin, right: margin }
            });
            yPos = pdf.lastAutoTable.finalY + 10;
          }

          // Outcome Breakdown
          if (outcomeData.outcome_breakdown) {
            if (yPos > pageHeight - 50) {
              pdf.addPage();
              yPos = margin;
            }

            pdf.setFontSize(12);
            pdf.text('Outcome Breakdown', margin, yPos);
            yPos += 5;

            const outcomeBreakdownData = Object.entries(outcomeData.outcome_breakdown)
              .map(([outcome, count]) => [
                outcome.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                String(count)
              ]);

            pdf.autoTable({
              startY: yPos,
              head: [['Outcome', 'Count']],
              body: outcomeBreakdownData,
              theme: 'striped',
              headStyles: { fillColor: [59, 130, 246], textColor: 255 },
              styles: { fontSize: 10, cellPadding: 4 },
              margin: { left: margin, right: margin }
            });
            yPos = pdf.lastAutoTable.finalY + 15;
          }
        }
      }

      // ==================== DAILY TRENDS SECTION ====================
      if (selectedSections.dailyTrends && trendsData?.daily_trends) {
        if (yPos > pageHeight - 80) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setTextColor(30, 30, 30);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Daily Trends', margin, yPos);
        yPos += 10;

        if (includeTables) {
          const trendsTableData = trendsData.daily_trends
            .slice(-14) // Last 14 days
            .map(day => [
              new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              String(day.calls || 0),
              `${day.avg_duration || 0}s`,
              `${day.success_rate || 0}%`
            ]);

          pdf.autoTable({
            startY: yPos,
            head: [['Date', 'Calls', 'Avg Duration', 'Success Rate']],
            body: trendsTableData,
            theme: 'striped',
            headStyles: { fillColor: [139, 92, 246], textColor: 255 },
            styles: { fontSize: 9, cellPadding: 3 },
            margin: { left: margin, right: margin }
          });
          yPos = pdf.lastAutoTable.finalY + 15;
        }
      }

      // ==================== FOOTER ====================
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Page ${i} of ${pageCount} | AIConneqtors Voice Analytics Report | Confidential`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }

      // Save the PDF
      const fileName = `AIConneqtors_Voice_Analytics_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      onClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  const selectedCount = Object.values(selectedSections).filter(Boolean).length;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-lg font-bold text-white">Export to PDF</h2>
              <p className="text-purple-200 text-sm">Select sections to include</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Section Selection */}
          <div>
            <h3 className="text-white font-medium mb-3">Report Sections</h3>
            <div className="space-y-2">
              {sections.map((section) => (
                <label
                  key={section.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedSections[section.id]
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSections[section.id]}
                    onChange={() => toggleSection(section.id)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${
                    selectedSections[section.id]
                      ? 'bg-purple-600'
                      : 'bg-gray-700'
                  }`}>
                    {selectedSections[section.id] && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <section.icon className={`w-5 h-5 ${
                    selectedSections[section.id] ? 'text-purple-400' : 'text-gray-500'
                  }`} />
                  <span className={selectedSections[section.id] ? 'text-white' : 'text-gray-400'}>
                    {section.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Output Options */}
          <div>
            <h3 className="text-white font-medium mb-3">Output Options</h3>
            <div className="flex gap-4">
              <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                includeTables
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-gray-700 hover:border-gray-600'
              }`}>
                <input
                  type="checkbox"
                  checked={includeTables}
                  onChange={() => setIncludeTables(!includeTables)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded flex items-center justify-center ${
                  includeTables ? 'bg-blue-600' : 'bg-gray-700'
                }`}>
                  {includeTables && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className={includeTables ? 'text-white' : 'text-gray-400'}>
                  Include Tables
                </span>
              </label>
            </div>
          </div>

          {/* Preview Info */}
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <FileText className="w-4 h-4 text-purple-400" />
              <span>
                Report will include <span className="text-white font-medium">{selectedCount}</span> sections
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-400 text-xs mt-2">
              <span>Time period: Last {days} days</span>
              <span className="text-gray-600">|</span>
              <span>Format: PDF with AIConneqtors branding</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-800/50 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={generatePDF}
            disabled={generating || selectedCount === 0}
            className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-medium transition-colors"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportPDFModal;
